import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiExternalLink, FiLink, FiPlus, FiTrash2, FiUserPlus, FiX } from 'react-icons/fi';
import { api } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ui/Toast';
import { Button, Card, Input, Select } from '../../../components/ui/Kit';
import Modal from '../../../components/ui/Modal';
import SelectLeadModal from '../../../components/Calendar/SelectLeadModal';
import SafeIcon from '../../../common/SafeIcon';

const EMPLOYMENT_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'employed', label: 'Employed' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'retired', label: 'Retired' },
  { value: 'student', label: 'Student' },
  { value: 'other', label: 'Other' },
];

const MARITAL_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'separated', label: 'Separated' },
  { value: 'domestic_partner', label: 'Domestic Partner' },
];

const RELATIONSHIP_OPTIONS = [
  { value: 'child', label: 'Child' },
  { value: 'stepchild', label: 'Stepchild' },
  { value: 'other', label: 'Other' },
];

const SPOUSE_VISIBLE = new Set(['married', 'domestic_partner']);

const PERSONAL_FIELDS = [
  'date_of_birth',
  'employment_status',
  'occupation',
  'marital_status',
  'spouse_first_name',
  'spouse_last_name',
  'spouse_email',
  'spouse_phone',
  'spouse_date_of_birth',
  'spouse_lead_id',
];

const cellInput =
  'w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

function leadDisplayName(lead) {
  if (!lead) return 'Linked lead';
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return name || 'Linked lead';
}

function personalPayloadFromFields(fields) {
  const emptyToNull = (v) => (v === '' || v === undefined ? null : v);
  return {
    date_of_birth: emptyToNull(fields.date_of_birth),
    employment_status: emptyToNull(fields.employment_status),
    occupation: emptyToNull(fields.occupation),
    marital_status: emptyToNull(fields.marital_status),
    spouse_first_name: emptyToNull(fields.spouse_first_name),
    spouse_last_name: emptyToNull(fields.spouse_last_name),
    spouse_email: emptyToNull(fields.spouse_email),
    spouse_phone: emptyToNull(fields.spouse_phone),
    spouse_date_of_birth: emptyToNull(fields.spouse_date_of_birth),
    spouse_lead_id: emptyToNull(fields.spouse_lead_id),
  };
}

function dependentPayloadFromRow(row) {
  return {
    full_name: row.full_name || '',
    date_of_birth: row.date_of_birth || null,
    relationship: row.relationship || 'child',
  };
}

export default function PersonalHouseholdTab({
  analysisId,
  agencyId,
  analysis,
  dependents,
  setDependents,
  scheduleParentSave,
  scheduleRowSave,
  setSaveStatus,
  markSavedBriefly,
  parentFieldsRef,
  dependentsRef,
  onAnalysisPatch,
}) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [, bump] = useState(0);
  const [addingDependent, setAddingDependent] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkedLead, setLinkedLead] = useState(null);
  const [loadingLinkedLead, setLoadingLinkedLead] = useState(false);

  const fields = useMemo(() => {
    const fromRef = parentFieldsRef.current || {};
    const merged = {};
    for (const key of PERSONAL_FIELDS) {
      merged[key] =
        fromRef[key] !== undefined ? fromRef[key] : analysis?.[key] ?? (key === 'spouse_lead_id' ? null : '');
    }
    return merged;
    // bump forces refresh after local ref mutations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis, parentFieldsRef, bump]);

  const showSpouse = SPOUSE_VISIBLE.has(fields.marital_status);

  useEffect(() => {
    const spouseLeadId = analysis?.spouse_lead_id;
    if (!spouseLeadId) {
      setLinkedLead(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingLinkedLead(true);
      try {
        const lead = await api.leads.getById(spouseLeadId);
        if (!cancelled) setLinkedLead(lead);
      } catch {
        if (!cancelled) setLinkedLead({ id: spouseLeadId, first_name: 'Linked', last_name: 'lead' });
      } finally {
        if (!cancelled) setLoadingLinkedLead(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysis?.spouse_lead_id]);

  const setParentField = (field, value) => {
    const nextFields = {
      ...(parentFieldsRef.current || {}),
      [field]: value,
    };
    parentFieldsRef.current = nextFields;
    bump((n) => n + 1);
    onAnalysisPatch?.({ [field]: value });
    scheduleParentSave(() => personalPayloadFromFields(parentFieldsRef.current || nextFields));
  };

  const onDependentChange = (rowId, field, value) => {
    setDependents((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      dependentsRef.current = next;
      return next;
    });
    scheduleRowSave('dependents', rowId, () => {
      const row = (dependentsRef.current || []).find((r) => r.id === rowId);
      return dependentPayloadFromRow(row || {});
    });
  };

  const addDependent = async () => {
    setAddingDependent(true);
    try {
      const row = await api.financialAnalysis.dependents.create(analysisId, {
        agency_id: agencyId,
        full_name: '',
        date_of_birth: null,
        relationship: 'child',
      });
      setDependents((prev) => {
        const next = [...prev, row];
        dependentsRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add dependent', message: err?.message });
    } finally {
      setAddingDependent(false);
    }
  };

  const confirmDeleteDependent = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.financialAnalysis.dependents.delete(deleteTarget.id);
      setDependents((prev) => {
        const next = prev.filter((r) => r.id !== deleteTarget.id);
        dependentsRef.current = next;
        return next;
      });
      setSaveStatus('saved');
      markSavedBriefly();
      setDeleteTarget(null);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete dependent', message: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const applySpouseLeadLink = async (lead) => {
    setLinking(true);
    try {
      const updated = await api.financialAnalysis.spouseLead.link(analysisId, lead.id);
      parentFieldsRef.current = {
        ...(parentFieldsRef.current || {}),
        spouse_lead_id: lead.id,
      };
      onAnalysisPatch?.({
        spouse_lead_id: lead.id,
        ...(updated || {}),
      });
      setLinkedLead(lead);
      markSavedBriefly();
      addToast({ type: 'success', title: 'Spouse linked', message: `${leadDisplayName(lead)} linked as spouse lead.` });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not link spouse lead', message: err?.message });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    setLinking(true);
    try {
      await api.financialAnalysis.spouseLead.unlink(analysisId);
      parentFieldsRef.current = {
        ...(parentFieldsRef.current || {}),
        spouse_lead_id: null,
      };
      onAnalysisPatch?.({ spouse_lead_id: null });
      setLinkedLead(null);
      bump((n) => n + 1);
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not unlink spouse lead', message: err?.message });
    } finally {
      setLinking(false);
    }
  };

  const handleCreateSpouseLead = async () => {
    const first = String(fields.spouse_first_name || '').trim();
    const last = String(fields.spouse_last_name || '').trim();
    if (!first) {
      addToast({
        type: 'error',
        title: 'Spouse first name required',
        message: 'Enter a spouse first name before creating a lead.',
      });
      return;
    }
    setLinking(true);
    try {
      const channel = user?.default_channel || 'IM';
      const lead = await api.leads.create({
        first_name: first,
        last_name: last || '',
        email: fields.spouse_email || null,
        phone: fields.spouse_phone || null,
        channel,
        lead_type: 'referral',
        agency_id: agencyId || user?.agency_id,
      });
      const updated = await api.financialAnalysis.spouseLead.link(analysisId, lead.id);
      parentFieldsRef.current = {
        ...(parentFieldsRef.current || {}),
        spouse_lead_id: lead.id,
      };
      onAnalysisPatch?.({
        spouse_lead_id: lead.id,
        ...(updated || {}),
      });
      setLinkedLead(lead);
      setCreateConfirmOpen(false);
      markSavedBriefly();
      addToast({
        type: 'success',
        title: 'Spouse lead created',
        message: `${leadDisplayName(lead)} created and linked.`,
      });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not create spouse lead', message: err?.message });
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Personal & Household</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Foundational client details used across the analysis and report.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Date of Birth"
            type="date"
            value={fields.date_of_birth || ''}
            onChange={(e) => setParentField('date_of_birth', e.target.value || null)}
          />
          <Select
            label="Employment Status"
            options={EMPLOYMENT_OPTIONS}
            value={fields.employment_status || ''}
            onChange={(e) => setParentField('employment_status', e.target.value || null)}
          />
          <Input
            label="Occupation"
            value={fields.occupation || ''}
            onChange={(e) => setParentField('occupation', e.target.value)}
            placeholder="e.g. Software Engineer"
          />
          <Select
            label="Marital Status"
            options={MARITAL_OPTIONS}
            value={fields.marital_status || ''}
            onChange={(e) => setParentField('marital_status', e.target.value || null)}
          />
        </div>
      </Card>

      {showSpouse && (
        <Card className="p-5 space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Spouse / Partner</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Optionally link the spouse as their own lead record.
              </p>
            </div>
            {fields.spouse_lead_id ? (
              <div className="flex flex-wrap items-center gap-2">
                {loadingLinkedLead ? (
                  <span className="text-xs text-gray-500">Loading linked lead…</span>
                ) : (
                  <Link
                    to={`/leads/${fields.spouse_lead_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium hover:bg-primary/15 transition-colors"
                  >
                    {leadDisplayName(linkedLead)}
                    <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                  </Link>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUnlink}
                  disabled={linking}
                  isLoading={linking}
                >
                  <SafeIcon icon={FiX} className="w-3.5 h-3.5 mr-1" />
                  Unlink
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLinkModalOpen(true)}
                  disabled={linking}
                >
                  <SafeIcon icon={FiLink} className="w-3.5 h-3.5 mr-1" />
                  Link Existing Lead
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCreateConfirmOpen(true)}
                  disabled={linking}
                >
                  <SafeIcon icon={FiUserPlus} className="w-3.5 h-3.5 mr-1" />
                  Create & Link Lead
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Spouse First Name"
              value={fields.spouse_first_name || ''}
              onChange={(e) => setParentField('spouse_first_name', e.target.value)}
            />
            <Input
              label="Spouse Last Name"
              value={fields.spouse_last_name || ''}
              onChange={(e) => setParentField('spouse_last_name', e.target.value)}
            />
            <Input
              label="Spouse Email"
              type="email"
              value={fields.spouse_email || ''}
              onChange={(e) => setParentField('spouse_email', e.target.value)}
            />
            <Input
              label="Spouse Phone"
              type="tel"
              value={fields.spouse_phone || ''}
              onChange={(e) => setParentField('spouse_phone', e.target.value)}
            />
            <Input
              label="Spouse Date of Birth"
              type="date"
              value={fields.spouse_date_of_birth || ''}
              onChange={(e) => setParentField('spouse_date_of_birth', e.target.value || null)}
            />
          </div>
        </Card>
      )}

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Dependents</h2>
            <p className="text-xs text-gray-500 mt-0.5">Children and other household dependents</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addDependent}
            disabled={addingDependent}
            isLoading={addingDependent}
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
            Add Dependent
          </Button>
        </div>

        {(dependents || []).length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No dependents yet.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2.5">Full Name</th>
                  <th className="px-3 py-2.5 w-40">Date of Birth</th>
                  <th className="px-3 py-2.5 w-40">Relationship</th>
                  <th className="px-3 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(dependents || []).map((row) => (
                  <tr key={row.id} className="bg-white">
                    <td className="px-3 py-2">
                      <input
                        className={cellInput}
                        value={row.full_name || ''}
                        onChange={(e) => onDependentChange(row.id, 'full_name', e.target.value)}
                        placeholder="Full name"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        className={cellInput}
                        value={row.date_of_birth || ''}
                        onChange={(e) =>
                          onDependentChange(row.id, 'date_of_birth', e.target.value || null)
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className={cellInput}
                        value={row.relationship || 'child'}
                        onChange={(e) => onDependentChange(row.id, 'relationship', e.target.value)}
                      >
                        {RELATIONSHIP_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        onClick={() => setDeleteTarget(row)}
                        aria-label="Remove dependent"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SelectLeadModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        agencyId={agencyId}
        onSelect={(lead) => {
          applySpouseLeadLink(lead);
        }}
      />

      <Modal
        isOpen={createConfirmOpen}
        onClose={() => !linking && setCreateConfirmOpen(false)}
        title="Create spouse as lead"
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-4">
          Create a new lead from the spouse fields
          {fields.spouse_first_name || fields.spouse_last_name
            ? ` (${[fields.spouse_first_name, fields.spouse_last_name].filter(Boolean).join(' ')})`
            : ''}{' '}
          and link it to this analysis. Channel defaults to{' '}
          <span className="font-medium">{user?.default_channel || 'IM'}</span>, lead type{' '}
          <span className="font-medium">Referral</span>.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setCreateConfirmOpen(false)} disabled={linking}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateSpouseLead} disabled={linking} isLoading={linking}>
            Create & Link
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Remove dependent"
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-4">
          Remove {deleteTarget?.full_name?.trim() || 'this dependent'} from the household?
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteDependent} disabled={deleting} isLoading={deleting}>
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
}
